import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";
import { tap } from "rxjs/operators";

import { DEF_EMPTY_RESPONSE } from "@app/core/constants/empty-filter-response.const";
import { DEFAULT_PAGE_INDEX } from "@app/core/constants/pagination.const";
import { Permission } from "@app/core/permission/enums/permission.enum";
import { PermissionService } from "@app/core/permission/services/permission.service";
import { EAppModule } from "@app/core/services/app-config.service";
import { AppConfigStoreService } from "@app/core/services/app-config-store.service";
import { DestroyService } from "@app/core/services/destroy.service";
import { TResponseFilter } from "@app/core/types/response.type";
import { ResultGlobalSearchByEntityComponent } from "@app/features/global-search/components/result-global-search-by-entity/result-global-search-by-entity.component";
import { UserProfileModule } from "@app/features/user-profile/user-profile.module";
import { SearchBarComponent } from "@app/shared/components/search-bar/search-bar.component";
import { SkeletonComponent } from "@app/shared/components/skeleton/skeleton.component";
import {
  EGlobalSearchEntity,
  IClaimSearchItem,
  IGlobalSearch,
} from "@app/shared/response-type/global-search-response.interface";
import { GlobalSearchApiService } from "@app/shared/services/global-search-api.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

const DEF_PAGE_ITEMS = 5;
const DEBOUNCE_CHANGE_INPUT = 300;
const POSITION_CONNECTED_OVERLAY_DEF: ConnectedPosition[] = [
  {
    originX: "center",
    originY: "bottom",
    overlayX: "center",
    overlayY: "top",
  },
  {
    originX: "center",
    originY: "bottom",
    overlayX: "center",
    overlayY: "top",
  },
];

enum EStatusGlobalSearch {
  PADDING = 1,
  SUCCESS,
  ERROR,
}

interface IGlobalSearchResult {
  response: TResponseFilter<IGlobalSearch>;
  status: EStatusGlobalSearch;
}

@Component({
  selector: "under-ui-global-search",
  templateUrl: "./global-search.component.html",
  styleUrl: "./global-search.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    UserProfileModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    AsyncPipe,
    SkeletonComponent,
    ResultGlobalSearchByEntityComponent,
    NgTemplateOutlet,
    SearchBarComponent,
  ],
  standalone: true,
  providers: [DestroyService],
})
export class GlobalSearchComponent implements OnInit {
  @Input() searchText?: string;

  showPanel$!: Observable<boolean>;
  globalResult: IGlobalSearchResult = {
    response: DEF_EMPTY_RESPONSE<IGlobalSearch>(),
    status: EStatusGlobalSearch.PADDING,
  };

  loading$ = new BehaviorSubject<boolean>(false);
  search = new FormControl<string | null>(null);

  readonly position = POSITION_CONNECTED_OVERLAY_DEF;
  readonly skeletonSize = Array.from({ length: DEF_PAGE_ITEMS });
  readonly globalStatusSearch = EStatusGlobalSearch;

  private isPanelVisible$ = new Subject<boolean>();
  private isPanelHidden$ = new Subject<boolean>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly destroy$: DestroyService,
    private readonly globalSearchApiService: GlobalSearchApiService,
    private readonly permissionService: PermissionService,
    private readonly appConfigStore: AppConfigStoreService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.showPanel$ = merge(this.isPanelHidden$, this.isPanelVisible$);

    this.loadingResultSearch();

    if (this.searchText) {
      this.search.setValue(this.searchText);
    }
  }

  onBackdropClick(): void {
    this.isPanelHidden$.next(false);
  }

  onOpenOverlay(): void {
    this.isPanelVisible$.next(true);
  }

  private loadingResultSearch(): void {
    const baseFilter = { page: DEFAULT_PAGE_INDEX, pageSize: DEF_PAGE_ITEMS };

    this.search.valueChanges
      .pipe(
        debounceTime(DEBOUNCE_CHANGE_INPUT),
        distinctUntilChanged(),
        filter(query => !!query),
        tap(() => this.loading$.next(true)),
        switchMap(query =>
          forkJoin([
            this.globalSearchApiService
              .search({ ...baseFilter, filters: { query: query as string } })
              .pipe(
                map(response => response.data),
                catchError(() => of(null)),
              ),
            this.searchClaims(query as string, baseFilter.pageSize),
          ]).pipe(
            map(([globalResponse, claims]) => this.toResult(globalResponse, claims)),
            finalize(() => this.loading$.next(false)),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(response => {
        this.globalResult = response;

        this.cdr.markForCheck();
      });
  }

  private isClaimSearchAvailable(): boolean {
    return (
      this.appConfigStore.enabledModules().includes(EAppModule.Claims) &&
      this.permissionService.hasPermission(Permission.VIEW_CLAIMS)
    );
  }

  private searchClaims(term: string, pageSize: number): Observable<IGlobalSearch[] | null> {
    if (!this.isClaimSearchAvailable()) {
      return of([]);
    }

    const canViewAllClaims = this.permissionService.hasPermission(Permission.VIEW_ALL_CLAIMS);

    return this.globalSearchApiService.searchClaims(term, pageSize, canViewAllClaims).pipe(
      map(response => (response.data?.items ?? []).map(claim => this.toGlobalSearchItem(claim))),
      catchError(() => of(null)),
    );
  }

  private toGlobalSearchItem(claim: IClaimSearchItem): IGlobalSearch {
    return {
      entityType: EGlobalSearchEntity.Claim,
      entityName: this.translate.instant("CLAIMS.SEARCH.CLAIMS"),
      entityId: claim.id,
      displayValue: claim.claimantName ? `${claim.number} — ${claim.claimantName}` : claim.number,
      createdAt: "",
      clientId: "",
    };
  }

  private toResult(
    globalResponse: TResponseFilter<IGlobalSearch> | null,
    claims: IGlobalSearch[] | null,
  ): IGlobalSearchResult {
    if (!globalResponse) {
      return { status: EStatusGlobalSearch.ERROR, response: DEF_EMPTY_RESPONSE<IGlobalSearch>() };
    }

    const claimItems = claims ?? [];

    return {
      status: EStatusGlobalSearch.SUCCESS,
      response: {
        ...globalResponse,
        items: [...claimItems, ...globalResponse.items],
        totalCount: globalResponse.totalCount + claimItems.length,
      },
    };
  }
}
